import React                    from 'react';
import ApiCollection            from '@widesk-core/models/ApiCollection';
import ContactContactGroupModel from '@/models/services/network/ContactContactGroupModel';
import ContactModel             from '@/models/services/network/ContactModel';
import EntityAssignManagerForm  from '@/pages/Entity/forms/EntityAssignManagerForm';
import EntityModel              from '@/models/services/fondations/EntityModel';
import Modal                    from '@/components/modal/Modal';
import { ModalData }            from '@/components/modal/Modal';
import { Text }                 from '@chakra-ui/react';
import { toaster }              from '@/components/ui/toaster';
import { useTranslation }       from 'react-i18next';

interface UseEntityManagersBoxParams {
	entity:             EntityModel;
	assignableContacts: ContactModel[];
	onSuccess:          () => void;
}

const useEntityManagersBox = ({ entity, assignableContacts, onSuccess }: UseEntityManagersBoxParams) => {
	const { t } = useTranslation();
	const [modalData, setModalData]     = React.useState<ModalData | undefined>();
	const [isModalOpen, setIsModalOpen] = React.useState(false);

	const closeModal = () => {
		setIsModalOpen(false);
		setModalData(undefined);
	};

	const openAssignModal = () => {
		setModalData({
			setIsOpen:      setIsModalOpen,
			titleComponent: <Text>{t('pages.entity.managers.modals.assign.title')}</Text>,
			bodyComponent:  (
				<EntityAssignManagerForm
					entity={entity}
					assignableContacts={assignableContacts}
					onSuccess={async () => { onSuccess(); closeModal(); }}
					onClose={closeModal}
				/>
			),
			onClose: closeModal,
		});
		setIsModalOpen(true);
	};

	const openDeleteModal = (manager: ContactModel) => {
		setModalData({
			model:          manager,
			isDangerous:    true,
			setIsOpen:      setIsModalOpen,
			titleComponent: <Text>{t('pages.entity.managers.modals.delete.title')}</Text>,
			bodyComponent:  <Text>{t('pages.entity.managers.modals.delete.message', { fullname: manager.fullName })}</Text>,
			onClose:        closeModal,
			onConfirm:      async () => {
				const ccgCollection = await new ApiCollection(ContactContactGroupModel)
					.setFilter('contact', manager.id)
					.list();
				const ccg = ccgCollection.first();
				if (ccg) await (ccg as unknown as ContactContactGroupModel).destroy();
			},
			onSuccess: () => {
				toaster.create({ title: t('pages.entity.managers.modals.delete.messages.success'), type: 'success' });
				onSuccess();
				closeModal();
			},
			onError: () => {
				toaster.create({ title: t('pages.entity.managers.modals.delete.messages.error'), type: 'error' });
			},
		});
		setIsModalOpen(true);
	};

	const modalElement = (
		<Modal
			model={modalData?.model}
			isDangerous={modalData?.isDangerous}
			isOpen={isModalOpen}
			setIsOpen={setIsModalOpen}
			titleComponent={modalData?.titleComponent}
			bodyComponent={modalData?.bodyComponent}
			onConfirm={modalData?.onConfirm}
			onClose={modalData?.onClose}
			onError={modalData?.onError}
			onSuccess={modalData?.onSuccess}
		/>
	);

	return { modalElement, openAssignModal, openDeleteModal };
};

export default useEntityManagersBox;
